import {CustomFieldType} from '@frontapp/workflows/custom_fields';
import {isEmpty} from 'lodash';
import React, {FC, ReactNode, useMemo, useState} from 'react';
import styled from 'styled-components';

import {useLocalize} from '../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {defineMessage, Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {Renderer, RendererOf} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {isDefined} from '../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {CustomFieldEnumValueModel} from '../../../../../libs/shared/core/src/models/api/customFieldEnumValueModel';
import {TextAreaBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/textAreaBox';
import {TextBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ButtonGroupSpacer} from '../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Dropdown, DropdownPlacement} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownFooter} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownFooter';
import {DropdownHeader} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownHeader';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {useAppDispatch} from '../../../../helpers/appReduxHelpers';
import {customFieldTypes} from '../../../../helpers/customFieldHelpers';
import {
  CustomFieldErrorCodesEnum,
  CustomFieldModel,
  CustomFieldResourceTypesEnum,
} from '../../../../models/api/customFieldModel';
import {computeEnumValuesErrors} from '../../../../settings/customFields/form/customFieldForm';
import {EnumValuesTable} from '../../../../settings/customFields/form/enumValuesTable';
import {createCustomFieldAsyncAction} from '../../../../store/data/customFields/customFieldsActions';
import {CustomFieldTypesDropdown} from './customFieldTypesDropdown';

/*
 * Constants.
 */

const dropdownWidth = 300; // px.
const dropdownHeight = 496; // px.

/*
 * Props.
 */

interface CreateCustomFieldDropdownProps {
  customFieldResourceType: CustomFieldResourceTypesEnum;
  types?: ReadonlyArray<CustomFieldType>;
  popoverProps: PopoverTransferProps;
  fieldName?: string;
  isShowingBackButton?: boolean;
  renderDropdownTitle: Renderer;
  onSelect: (model: CustomFieldModel) => void;
  onCancelClick: () => void;
  renderTypesDisabledTooltip?: RendererOf<CustomFieldType>;
  renderTypesDropdownItemDisabledTooltip?: Renderer;
  placement?: DropdownPlacement;
}

/*
 * Style.
 */

const StyledDropdownContentDiv = styled.div`
  gap: 8px;
  display: flex;
  flex-flow: column;
  padding: 20px 20px 32px 20px;
  overflow-y: auto;
`;

const StyledDropdownFooter = styled(DropdownFooter)`
  padding: 4px 20px 20px 20px;
  border-top: none;
`;

const StyledErrorWrapperDiv = styled.div`
  color: ${(p) => p.theme.palette.red.shade40};
  text-align: left;
`;

/*
 * Component.
 */

export const CreateCustomFieldDropdown: FC<CreateCustomFieldDropdownProps> = (props) => {
  const {
    customFieldResourceType,
    types,
    popoverProps,
    fieldName,
    isShowingBackButton,
    onCancelClick,
    onSelect,
    renderDropdownTitle,
    renderTypesDisabledTooltip,
    renderTypesDropdownItemDisabledTooltip,
    placement = 'top-start',
  } = props;
  const dispatch = useAppDispatch();
  const localize = useLocalize();
  const [customFieldName, setCustomFieldName] = useState(fieldName || '');
  const [customFieldDescription, setCustomFieldDescription] = useState('');
  const availableTypes = types || customFieldTypes;
  const [customFieldType, setCustomFieldType] = useState(availableTypes[0]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createError, setCreateError] = useState<ReactNode>();
  const [enumValues, setEnumValues] = useState<ReadonlyArray<CustomFieldEnumValueModel>>([
    {value: '', label: ''},
  ]);
  const enumValuesErrors = useMemo(() => computeEnumValuesErrors(enumValues), [enumValues]);

  const isEnumType = customFieldType === CustomFieldType.ENUM;
  const canCreate = isEmpty(enumValuesErrors) && customFieldName.trim() !== '';

  const onCloseClick = () => {
    if (isSubmitting) {
      return;
    }
    onCancelClick();
  };

  const onCreateAsync = async () => {
    setCreateError(undefined);
    setIsSubmitting(true);
    try {
      const customField = await dispatch(
        createCustomFieldAsyncAction(
          {
            name: customFieldName.trim(),
            description: customFieldDescription,
            type: customFieldType,
            enum_values: isEnumType ? enumValues.filter(isDefined) : undefined,
          },
          customFieldResourceType,
        ),
      );
      onSelect(customField);
    } catch (error) {
      if (error.body?.code === CustomFieldErrorCodesEnum.CUSTOM_FIELD_ALREADY_EXIST) {
        setCreateError(
          <Trans id="create-custom-field-conflict-error">A custom field with that name already exists</Trans>,
        );
      } else {
        setCreateError(<Trans id="create-custom-field-error">An error occurred</Trans>);
      }
    }
    setIsSubmitting(false);
  };

  return (
    <Dropdown
      {...popoverProps}
      placement={placement}
      hasBackground
      width={dropdownWidth}
      maxHeight={dropdownHeight}
      onRequestClose={onCloseClick}
    >
      <DropdownHeader
        title={renderDropdownTitle()}
        leftIcon={renderBackButton(onCloseClick, isShowingBackButton)}
      />
      <StyledDropdownContentDiv>
        <TextBox
          value={customFieldName}
          label={<Trans id="create-custom-field-name-title">Name</Trans>}
          isRequired
          shouldAutoFocus
          onChange={(val) => setCustomFieldName(val)}
        />
        <TextAreaBox
          label={<Trans id="create-custom-field-description-title">Description</Trans>}
          placeholder={localize(
            defineMessage({
              id: 'create-custom-field-description-placeholder',
              message: 'What will this custom field be about?',
            }),
          )}
          shouldAllowResize
          onChange={(val) => setCustomFieldDescription(val)}
        />
        <CustomFieldTypesDropdown
          selectedType={customFieldType}
          availableTypes={availableTypes}
          onSelect={(type) => setCustomFieldType(type)}
          renderDisabledTooltip={(type) => renderTypesDisabledTooltip && renderTypesDisabledTooltip(type)}
          renderDropdownItemDisabledTooltip={renderTypesDropdownItemDisabledTooltip}
        />
        {isEnumType && (
          <EnumValuesTable
            enumValues={enumValues}
            enumValuesErrors={enumValuesErrors}
            onUpdateValues={setEnumValues}
          />
        )}
        {createError && <StyledErrorWrapperDiv>{createError}</StyledErrorWrapperDiv>}
      </StyledDropdownContentDiv>
      <StyledDropdownFooter>
        <ButtonGroup>
          <ButtonGroupSpacer />
          <Button onClick={onCloseClick}>
            <Trans id="create-custom-field-cancel-button">Cancel</Trans>
          </Button>
          <Button
            variant={ButtonVariantsEnum.PRIMARY}
            isDisabled={!canCreate || isSubmitting}
            onClick={onCreateAsync}
          >
            <Trans id="create-custom-field-create-button">Create</Trans>
          </Button>
        </ButtonGroup>
      </StyledDropdownFooter>
    </Dropdown>
  );
};

/*
 * Helpers.
 */

function renderBackButton(onBackClick: () => void, isShowingBackButton?: boolean) {
  if (!isShowingBackButton) {
    return null;
  }

  return (
    <IconButton iconName="chevronLeftSmall" label={null} onClick={onBackClick} size={VisualSizesEnum.SMALL} />
  );
}
